import { z } from 'zod'

// ===========================================
// Common Schemas with Spanish Error Messages
// ===========================================

const requiredString = (field: string) =>
  z.string({ required_error: `${field} es requerido` }).min(1, `${field} es requerido`)

const optionalString = z.string().optional().nullable()

// ===========================================
// Enums
// ===========================================

export const contractTypeSchema = z.enum(['residential', 'commercial'], {
  required_error: 'Tipo de contrato es requerido',
})
export type ContractTypeValue = z.infer<typeof contractTypeSchema>

export const intakeStatusSchema = z.enum(['draft', 'complete', 'approved'], {
  required_error: 'Estado es requerido',
})
export type IntakeStatusValue = z.infer<typeof intakeStatusSchema>

export const partyRoleSchema = z.enum(['tenant', 'guarantor', 'owner', 'beneficiary'], {
  required_error: 'Rol es requerido',
})
export type PartyRoleValue = z.infer<typeof partyRoleSchema>

export const personTypeSchema = z.enum(['natural', 'legal'], {
  required_error: 'Tipo de persona es requerido',
})
export type PersonTypeValue = z.infer<typeof personTypeSchema>

export const propertyTypeSchema = z.enum(
  ['apartamento', 'casa', 'local', 'oficina', 'bodega', 'lote', 'parqueadero'],
  {
    required_error: 'Tipo de inmueble es requerido',
  }
)
export type PropertyTypeValue = z.infer<typeof propertyTypeSchema>

export const accountTypeSchema = z.enum(['ahorros', 'corriente'], {
  required_error: 'Tipo de cuenta es requerido',
})
export type AccountTypeValue = z.infer<typeof accountTypeSchema>

// ===========================================
// Property Schema
// ===========================================

export const propertySchema = z.object({
  address: requiredString('Direccion del inmueble'),
  buildingName: optionalString,
  propertyType: propertyTypeSchema,
  stratum: z.coerce
    .number({ required_error: 'Estrato es requerido' })
    .int()
    .min(1, 'Estrato debe estar entre 1 y 6')
    .max(6, 'Estrato debe estar entre 1 y 6'),
  area: z.coerce
    .number({ required_error: 'Area es requerida' })
    .positive('Area debe ser un numero positivo'),
  city: requiredString('Ciudad'),
  parking: z.coerce.number().int().min(0).default(0),
  storage: z.coerce.number().int().min(0).default(0),
})
export type PropertyData = z.infer<typeof propertySchema>

// ===========================================
// Terms Schema
// ===========================================

export const termsSchema = z.object({
  monthlyRent: z.coerce
    .number({ required_error: 'Canon mensual es requerido' })
    .positive('Canon debe ser un numero positivo'),
  administrationFee: z.coerce.number().min(0).default(0),
  adminFeePaidBy: z.enum(['tenant', 'owner']).default('tenant'),
  startDate: requiredString('Fecha de inicio'),
  endDate: requiredString('Fecha de finalizacion'),
  durationMonths: z.coerce
    .number({ required_error: 'Duracion es requerida' })
    .int()
    .positive('Duracion debe ser mayor a 0'),
  rentIncreaseFormula: z.string().default('IPC'),
  gracePeriodDays: z.coerce.number().int().min(0).default(5),
})
export type TermsData = z.infer<typeof termsSchema>

// ===========================================
// Insurance Schema
// ===========================================

export const insuranceSchema = z
  .object({
    filingNumber: optionalString,
    date: optionalString,
    provider: optionalString,
  })
  .optional()
  .nullable()
export type InsuranceData = z.infer<typeof insuranceSchema>

// ===========================================
// Commission Schema
// ===========================================

export const commissionSchema = z.object({
  percentage: z.coerce.number().min(0).max(100).default(8),
  comprehensiveCoverage: z.boolean().default(false),
  utilitiesCoverage: z.boolean().default(false),
  damagesCoverage: z.boolean().default(false),
})
export type CommissionData = z.infer<typeof commissionSchema>

// ===========================================
// Banking Schema
// ===========================================

export const bankingSchema = z.object({
  accountNumber: requiredString('Numero de cuenta'),
  bankName: requiredString('Nombre del banco'),
  accountType: accountTypeSchema,
  accountHolderName: requiredString('Nombre del titular'),
  accountHolderId: requiredString('Cedula del titular'),
})
export type BankingData = z.infer<typeof bankingSchema>

// ===========================================
// Documents Schema
// ===========================================

export const documentsSchema = z
  .object({
    idCopy: optionalString,
    chamberOfCommerce: optionalString,
    rut: optionalString,
  })
  .optional()
  .nullable()
export type DocumentsData = z.infer<typeof documentsSchema>

// ===========================================
// Base Party Fields (shared by all parties)
// ===========================================

const partyBaseFields = {
  id: z.string().optional(),
  role: partyRoleSchema,
  sortOrder: z.number().int().min(0).default(0),
  firstName: requiredString('Nombre'),
  lastName: requiredString('Apellido'),
  idNumber: z
    .string({ required_error: 'Numero de identificacion es requerido' })
    .min(5, 'Numero de identificacion muy corto'),
  idIssuedAt: optionalString,
  profession: optionalString,
  phone: z
    .string({ required_error: 'Telefono es requerido' })
    .min(7, 'Telefono muy corto')
    .max(20, 'Telefono muy largo'),
  address: requiredString('Direccion'),
  city: requiredString('Ciudad'),
  email: z
    .string({ required_error: 'Correo electronico es requerido' })
    .email('Correo electronico invalido'),
  invoiceEmail: z.string().email('Correo invalido').optional().or(z.literal('')),
  banking: bankingSchema.optional().nullable(),
  documents: documentsSchema,
  dataAuthorizationAccepted: z.boolean().default(false),
  referenceCheckAuthorized: z.boolean().default(false),
  // Legal entity fields (optional for natural persons)
  personType: personTypeSchema,
  companyName: optionalString,
  nit: optionalString,
  legalRepName: optionalString,
  legalRepId: optionalString,
}

// ===========================================
// Party Schema
// ===========================================

export const partySchema = z.object(partyBaseFields)

export type PartyFormData = z.infer<typeof partySchema>

// ===========================================
// Step 1: Contract Type Schema
// ===========================================

export const step1Schema = z.object({
  contractType: contractTypeSchema,
  guaranteedRent: z.boolean().default(false),
})
export type Step1Data = z.infer<typeof step1Schema>

// ===========================================
// Step 2: Property Schema
// ===========================================

export const step2Schema = z.object({
  property: propertySchema,
})
export type Step2Data = z.infer<typeof step2Schema>

// ===========================================
// Step 3: Tenant Schema
// ===========================================

export const step3Schema = z.object({
  tenant: partySchema,
})
export type Step3Data = z.infer<typeof step3Schema>

// ===========================================
// Step 4: Guarantors Schema (0-3)
// ===========================================

export const step4Schema = z.object({
  guarantors: z.array(partySchema).max(3, 'Maximo 3 deudores solidarios'),
})
export type Step4Data = z.infer<typeof step4Schema>

// ===========================================
// Step 5: Owners Schema (1-2)
// ===========================================

export const step5Schema = z.object({
  owners: z
    .array(partySchema.extend({ banking: bankingSchema }))
    .min(1, 'Minimo 1 propietario')
    .max(2, 'Maximo 2 propietarios'),
})
export type Step5Data = z.infer<typeof step5Schema>

// ===========================================
// Step 6: Terms Schema
// ===========================================

export const step6Schema = z.object({
  terms: termsSchema,
})
export type Step6Data = z.infer<typeof step6Schema>

// ===========================================
// Complete Intake Form Schema
// ===========================================

export const intakeFormSchema = z.object({
  // Step 1
  contractType: contractTypeSchema,
  guaranteedRent: z.boolean().default(false),

  // Step 2
  property: propertySchema,

  // Step 3 - Tenant (single)
  tenant: partySchema,

  // Step 4 - Guarantors (0-3)
  guarantors: z.array(partySchema).max(3, 'Maximo 3 deudores solidarios').default([]),

  // Step 5 - Owners (1-2)
  owners: z
    .array(partySchema.extend({ banking: bankingSchema }))
    .min(1, 'Minimo 1 propietario')
    .max(2, 'Maximo 2 propietarios'),

  // Step 6 - Terms
  terms: termsSchema,

  // Optional fields
  insurance: insuranceSchema,
  commission: commissionSchema,
  additionalClauses: z.string().optional().nullable(),
  capturedBy: z.string().optional().nullable(),
  rentedBy: z.string().optional().nullable(),
})

export type IntakeFormData = z.infer<typeof intakeFormSchema>

// ===========================================
// API Request/Response Schemas
// ===========================================

export const createIntakeSchema = intakeFormSchema

export const updateIntakeSchema = intakeFormSchema.partial()

export const intakeFiltersSchema = z.object({
  status: intakeStatusSchema.optional(),
  contractType: contractTypeSchema.optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  sortBy: z.enum(['createdAt', 'updatedAt', 'orderNumber']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
})

export type IntakeFilters = z.infer<typeof intakeFiltersSchema>

// ===========================================
// Party API Schemas
// ===========================================

export const addPartySchema = partySchema

export const updatePartySchema = partySchema.partial().extend({
  id: z.string(),
})

// ===========================================
// Document Upload Schemas
// ===========================================

export const documentTypeSchema = z.enum(['idCopy', 'chamberOfCommerce', 'rut'])
export type DocumentType = z.infer<typeof documentTypeSchema>

export const uploadDocumentSchema = z.object({
  documentType: documentTypeSchema,
  filename: z.string(),
  contentType: z.string(),
})

export type UploadDocumentRequest = z.infer<typeof uploadDocumentSchema>
